import { addDays, fmtTime, parseLocal, pad, startOfWeek, todayStr, ymd } from "@/lib/dates";

const START_H = 7;
const END_H = 22;
const HOUR_PX = 52;
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function rangeFor(view, anchor) {
  if (view === "day") return [anchor, addDays(anchor, 1)];
  if (view === "week") { const s = startOfWeek(anchor); return [s, addDays(s, 7)]; }
  const s = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  return [s, addDays(s, 42)];
}

const chipCls = (t) => {
  if (t.done) return "bg-slate-100 text-slate-400 line-through border-slate-300";
  if (t.due_at.slice(0, 10) < todayStr()) return "bg-rose-50 text-rose-700 border-rose-400";
  return t.kind === "visit" ? "bg-amber-50 text-amber-800 border-[hsl(var(--brand-gold))]" : "bg-blue-50 text-[hsl(var(--brand-navy))] border-[hsl(var(--brand-navy))]";
};

/** Outlook-style calendar: Day / Week time grid and a Month grid. */
export default function CalendarView({ view, anchor, tasks, onTaskClick, onSlotClick, onDayClick }) {
  const byDay = {};
  tasks.forEach((t) => { (byDay[t.due_at.slice(0, 10)] ||= []).push(t); });
  if (view === "month") return <MonthGrid anchor={anchor} byDay={byDay} onTaskClick={onTaskClick} onDayClick={onDayClick} />;
  const [s] = rangeFor(view, anchor);
  const days = Array.from({ length: view === "day" ? 1 : 7 }, (_, i) => addDays(s, i));
  return <TimeGrid days={days} byDay={byDay} onTaskClick={onTaskClick} onSlotClick={onSlotClick} />;
}

function layoutLanes(list) {
  const sorted = [...list].sort((a, b) => a.due_at.localeCompare(b.due_at));
  const laneEnds = [];
  const placed = sorted.map((t) => {
    const d = parseLocal(t.due_at);
    const start = d.getHours() * 60 + d.getMinutes();
    let lane = laneEnds.findIndex((e) => e <= start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
    laneEnds[lane] = start + 45;
    return { t, start, lane };
  });
  return { placed, lanes: Math.max(1, laneEnds.length) };
}

function TimeGrid({ days, byDay, onTaskClick, onSlotClick }) {
  const hours = Array.from({ length: END_H - START_H }, (_, i) => START_H + i);
  const today = todayStr();
  const now = new Date();
  const nowTop = ((now.getHours() - START_H) + now.getMinutes() / 60) * HOUR_PX;
  return (
    <div className="soft-card overflow-hidden" data-testid="calendar-time-grid">
      <div className="grid border-b border-slate-100" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0,1fr))` }}>
        <div />
        {days.map((d, i) => {
          const isToday = ymd(d) === today;
          return (
            <div key={i} className="border-l border-slate-100 px-2 py-2 text-center" data-testid={`cal-day-head-${ymd(d)}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{DOW[(d.getDay() + 6) % 7]}</div>
              <div className={`mx-auto mt-0.5 grid h-8 w-8 place-items-center rounded-full text-sm font-bold ${isToday ? "bg-navy" : "text-navy"}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      <div className="max-h-[62vh] overflow-y-auto">
        <div className="relative grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0,1fr))` }}>
          <div>
            {hours.map((h) => (
              <div key={h} style={{ height: HOUR_PX }} className="pr-2 text-right text-[10px] font-medium text-slate-400 -translate-y-1.5">
                {h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`}
              </div>
            ))}
          </div>
          {days.map((d, di) => {
            const key = ymd(d);
            const { placed, lanes } = layoutLanes(byDay[key] || []);
            return (
              <div key={di} className={`relative border-l border-slate-100 ${key === today ? "bg-amber-50/30" : ""}`}>
                {hours.map((h) => (
                  <button
                    key={h}
                    data-testid={`cal-slot-${key}-${h}`}
                    onClick={() => onSlotClick(`${key}T${pad(h)}:00`)}
                    style={{ height: HOUR_PX }}
                    className="block w-full border-b border-slate-50 transition-colors hover:bg-slate-50"
                    aria-label={`Add task ${key} ${h}:00`}
                  />
                ))}
                {key === today && nowTop > 0 && nowTop < hours.length * HOUR_PX && (
                  <div className="pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-rose-500" style={{ top: nowTop }}>
                    <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-rose-500" />
                  </div>
                )}
                {placed.map(({ t, start, lane }) => {
                  const top = Math.min(Math.max(0, (start / 60 - START_H) * HOUR_PX), hours.length * HOUR_PX - 28);
                  return (
                    <button
                      key={t.id}
                      data-testid={`cal-task-${t.id}`}
                      onClick={() => onTaskClick(t)}
                      className={`absolute z-[5] overflow-hidden rounded-md border-l-[3px] px-1.5 py-1 text-left text-[11px] leading-tight shadow-sm transition-transform hover:-translate-y-px hover:shadow ${chipCls(t)}`}
                      style={{ top, height: HOUR_PX * 0.78, left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)` }}
                    >
                      <div className="font-semibold">{fmtTime(t.due_at)}</div>
                      <div className="truncate">{t.title}</div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthGrid({ anchor, byDay, onTaskClick, onDayClick }) {
  const [s] = rangeFor("month", anchor);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(s, i));
  const today = todayStr();
  return (
    <div className="soft-card overflow-hidden" data-testid="calendar-month-grid">
      <div className="grid grid-cols-7 border-b border-slate-100">
        {DOW.map((d) => <div key={d} className="py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d) => {
          const key = ymd(d);
          const list = (byDay[key] || []).sort((a, b) => a.due_at.localeCompare(b.due_at));
          const inMonth = d.getMonth() === anchor.getMonth();
          return (
            <div key={key} className={`min-h-[92px] border-b border-l border-slate-50 p-1 ${inMonth ? "" : "bg-slate-50/60"}`}>
              <button
                data-testid={`cal-month-day-${key}`}
                onClick={() => onDayClick(d)}
                className={`mb-1 grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold hover:bg-slate-100 ${key === today ? "bg-navy" : inMonth ? "text-navy" : "text-slate-300"}`}
              >
                {d.getDate()}
              </button>
              <div className="space-y-0.5">
                {list.slice(0, 3).map((t) => (
                  <button key={t.id} data-testid={`cal-task-${t.id}`} onClick={() => onTaskClick(t)} className={`block w-full truncate rounded border-l-2 px-1 py-0.5 text-left text-[10px] ${chipCls(t)}`}>
                    {fmtTime(t.due_at)} {t.title}
                  </button>
                ))}
                {list.length > 3 && (
                  <button onClick={() => onDayClick(d)} className="px-1 text-[10px] font-semibold text-slate-500 hover:underline">+{list.length - 3} more</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
